import { Spinner, Typography } from '@tempmee/react';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Box, DataGrid, Pagination } from '~/commonComponents';
import { DashboardJobsStatus, DashboardJobsType, IDashboardJob } from '~/domain/DashboardJobs';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import { FilterStatusEnum } from '~/utils/Enum';
import { useJobList } from '../../hooks';
import { DataGridCommonColumn } from '../DataGrid.column';
import { EmptyMessage } from '../EmptyMessage';

interface IScheduledTabProps {
  jobType: DashboardJobsType;
}

export const ScheduledTab = ({ jobType }: IScheduledTabProps) => {
  const navigate = useNavigate();

  const isEnforcePermanentJobV2 = useRef(getShouldEnforcePermanentJobV2()).current;

  const { data, isInitialLoading, isLoading, methods, pagination } = useJobList({
    status: DashboardJobsStatus.FILLED,
    jobType,
  });

  const handleRowClick = useCallback(
    (item: IDashboardJob) => {
      navigate(`/dashboard/job/${item.id}`);
    },
    [navigate],
  );

  if (isInitialLoading) {
    return (
      <Box marginTop={32}>
        <Spinner centered variant="default" size="md" />
      </Box>
    );
  }

  return (
    <DataGrid
      EmptyComponent={<EmptyMessage type={FilterStatusEnum.SCHEDULED} />}
      PaginationComponent={
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={methods.setCurrentPage}
          maxButtons={5}
        />
      }
      columns={[
        DataGridCommonColumn.Office,
        ...(isEnforcePermanentJobV2
          ? [
              {
                ...DataGridCommonColumn.Type,
                width: '160px',
              },
            ]
          : []),
        {
          key: 'professional',
          header: 'Professional',
          width: '180px',
          render: (item) => (
            <Typography as="span" bold numberOfLines={1}>
              {item.professional
                ? [item.professional.firstName, item.professional.lastName].join(' ')
                : ''}
            </Typography>
          ),
        },
        {
          ...DataGridCommonColumn.Details,
          width: '200px',
        },
        DataGridCommonColumn.Profession,
        DataGridCommonColumn.Rate,
        DataGridCommonColumn.Action,
      ]}
      data={data}
      isLoading={isLoading}
      keyExtractor={(item) => item.id}
      onRowClick={handleRowClick}
    />
  );
};
